import React, { useEffect, useRef, ReactNode } from "react";

// 3rd parties
import { useSelector } from "react-redux";

import L from "leaflet";
import "leaflet-rotate";
import "leaflet-control-geocoder";
import "leaflet.fullscreen";

// owner
import useCustomMap from "./useCustomMap";
import { LocationIQ } from "./Geocoders/LocationIQ";
import { LayerUnwired } from "./Layers/LayerUnwired";
import { LayerNameUtil } from "./utils/LayerNameUtil";

import { CurrentLocation } from "./Marker/CurrentLocation";
import { ControlOverview } from "./Marker/ControlOverview";

import CombinedShapeIcon from "@images/map/combined-shape.png";
import { ControlBoundFilter } from "./Filters/ControlBoundFilter";

// Types
import { RootState } from "@/types/RootState";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";


interface CommonMapReactOptions {
  baseLayerDefault?: string;
  center?: [number, number] | null;
  zoom?: number;
  zoomPosition?: "topleft" | "topright" | "bottomleft" | "bottomright";
  hideCurrentLocation?: boolean;
  hideOverview?: boolean;
  hideZoomControl?: boolean;
  searchBox?: boolean;
  hideFullScreen?: boolean;
  showBoundFilter?: boolean;
  showGeozoneLayer?: boolean;
  showNetworkLayer?: boolean;
  rotate?: boolean;
  touchRotate?: boolean;
  bearing?: number;
  naturalBearing?: number;
  rotateControl?: {
    position?: "topleft" | "topright" | "bottomleft" | "bottomright";
    closeOnZeroBearing?: boolean;
  } | boolean;
}

interface CommonMapReactProps {
  width?: string;
  height?: number | string;
  options: CommonMapReactOptions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapRef: React.MutableRefObject<any>;
  onMapLoaded?: () => void;
  children?: ReactNode;
  disableInteractions?: boolean;
  /** Extra class on the map container, e.g. "standalone-map" for full-page maps. */
  className?: string;
}

/**
 * A DateRangeWidget component for selecting date ranges.
 *
 * @param {string} width - width of map.
 * @param {string} height - height of map.
 * @param {moment} value.start - The start of the date range.
 * @param {moment} value.end - The end of the date range.
 *
 * @param {object} options - Override default display modes. (default: {day: true, week: true, month: true, year: true, custom: true, all: false, last90d: false})
 * @param {string} options.baseLayerDefault - the base layer default of map. value: Satellite  || stress.
 * @param {array} options.center - a center of map. value: [50, 10]
 * @param {number} options.zoom - a zoom of map. value: 2.
 * @param {string} options.zoomPosition - zoom control position. value: 'topright'.
 * @param {boolean} options.hideCurrentLocation - a flag to hide current location button.
 * @param {boolean} options.hideOverview - a flag to hide overview button.
 * @param {boolean} options.searchBox - a flag to show search box.
 * @param {boolean} options.hideFullScreen - a flag to hide fullScreen button.
 *
 * @param {useRef} mapRef - a ref hook.
 *
 * @param {function} onMapLoaded - a map loaded callback
 *
 * @param {ReacChildren} children - children component.
 *
 * @returns {JSX.Element} The rendered DateRangeWidget component.
 *
 * @example
 * // Usage with custom mode and hidden buttons:
 * <CommonMapReact
 *  mapRef={map}
 *  width="100%"
 *  height={"100vh"}
 *  options={{
 *          hideCurrentLocation: false,
 *          hideOverview: false,
 *          searchBox: true,
 *          showGeozoneLayer: true,
 *          showNetworkLayer: true,
 *        }}
 *    onMapLoaded={() => {
 *      setMapLoaded(true);
 *    }}
 * >
 *  <div>Cluster</div>
 *  <div>Contol layers</div>
 * </CommonMapReact>
 */
const CommonMapReact: React.FC<CommonMapReactProps> = ({
  width = "100%",
  height,
  options,
  mapRef,
  onMapLoaded,
  children,
  disableInteractions = false, // New prop to disable interactions
  className = "",
}) => {
  if (!options) {
    throw new Error('CommonMapReact: options prop is required but was undefined. This component requires an options object to be passed.');
  }
  
  const loginInfo = useSelector((state: RootState) => state.user);

  // const [mapLoaded, setMapLoaded] = useState(true);

  const domMap = useRef<HTMLDivElement>(null);
  const CustomMap = useCustomMap();
  const key = "pk.6ed9cebcc44f553e3a45117cb9cb2ce4";

  useEffect(() => {
    const proxy = loginInfo.mapProxyEnabled;

    const streets = LayerUnwired({ proxy: proxy, scheme: "streets" });
    streets.name = translate(LanguageKey.street_key);

    const satellite = LayerUnwired({ proxy: proxy, scheme: "satellite" });
    satellite.name = LayerNameUtil.Satellite;

    let _default = streets;

    if (options.baseLayerDefault === LayerNameUtil.Satellite) {
      _default = satellite;
    }

    const _center = options.center || [50, 10];
    const _zoom = options.zoom || 2;

    // Build map options including rotation support
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapOptions: any = {
      attributionControl: false, // disable default attribution
      // Explicitly disable rotation by default (leaflet-rotate adds it when imported)
      rotate: false,
      rotateControl: false,
    };

    // Store rotation control options for later - we'll add it manually after zoom control
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let rotateControlOptions: any = null;

    // Add rotation options only if explicitly enabled
    if (options.rotate) {
      mapOptions.rotate = true;
      mapOptions.touchRotate = options.touchRotate !== false;
      mapOptions.shiftKeyRotate = true; // Shift + scroll wheel to rotate
      mapOptions.boxZoom = false; // Disable box zoom (Shift+drag) to avoid conflict
      if (options.bearing !== undefined) {
        mapOptions.bearing = options.bearing;
      }
      // Don't auto-add rotateControl - we'll add it manually after zoom control
      mapOptions.rotateControl = false;
      // Store options for manual addition
      rotateControlOptions = options.rotateControl !== undefined
        ? options.rotateControl
        : { position: "topright", closeOnZeroBearing: false };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapRef.current = new (CustomMap as any)(domMap.current, mapOptions);

    mapRef.current.setView(_center, _zoom).addLayer(_default);

    mapRef.current.baseLayers = [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseLayers: { [key: string]: any } = {};
    baseLayers[LayerNameUtil.Street] = streets;
    baseLayers[LayerNameUtil.Satellite] = satellite;

    if (!options.hideZoomControl) {
      // keep the zoomControl as default and move it to top right
      mapRef.current.zoomControl.setPosition(
        options.zoomPosition || "topright"
      );
    } else {
      mapRef.current.zoomControl.remove(); // Hide the zoom control
    }

    // Add rotate control AFTER zoom control so it appears below it
    if (rotateControlOptions) {
      // If naturalBearing is provided, create a custom control that cycles between north and natural bearing
      if (options.naturalBearing !== undefined && options.naturalBearing !== 0) {
        const naturalBearing = options.naturalBearing;

        // Create custom rotate control that cycles between north (0) and natural bearing
        const CustomRotateControl = L.Control.extend({
          options: {
            position: rotateControlOptions.position || "topright",
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onAdd: function(map: any) {
            const container = L.DomUtil.create("div", "leaflet-control-rotate leaflet-bar");
            const arrow = L.DomUtil.create("span", "leaflet-control-rotate-arrow");

            arrow.style.backgroundImage = `url("data:image/svg+xml;charset=utf-8,%3Csvg width='29' height='29' viewBox='0 0 29 29' xmlns='http://www.w3.org/2000/svg' fill='%23333'%3E%3Cpath d='M10.5 14l4-8 4 8h-8z'/%3E%3Cpath d='M10.5 16l4 8 4-8h-8z' fill='%23ccc'/%3E%3C/svg%3E")`;
            arrow.style.cursor = "pointer";
            arrow.style.display = "block";
            arrow.style.width = "100%";
            arrow.style.height = "100%";
            arrow.style.backgroundRepeat = "no-repeat";
            arrow.style.backgroundPosition = "50%";

            const link = L.DomUtil.create("a", "leaflet-control-rotate-toggle", container);
            link.appendChild(arrow);
            link.href = "#";
            link.title = translate(LanguageKey.toggle_map_rotation_key);

            // Track current state: true = at natural bearing, false = at north
            let isAtNaturalBearing = true;

            // Update arrow rotation based on current bearing
            const updateArrow = () => {
              const bearing = map.getBearing();
              arrow.style.transform = "rotate(" + bearing + "deg)";
            };

            // Click handler to cycle between north and natural bearing
            L.DomEvent
              .on(link, "dblclick", L.DomEvent.stopPropagation)
              .on(link, "click", L.DomEvent.stop)
              .on(link, "click", function() {
                if (isAtNaturalBearing) {
                  map.setBearing(0);
                  isAtNaturalBearing = false;
                } else {
                  map.setBearing(naturalBearing);
                  isAtNaturalBearing = true;
                }
              });

            map.on("rotate", updateArrow);
            updateArrow();

            return container;
          },
        });

        mapRef.current.rotateControl = new CustomRotateControl();
        mapRef.current.addControl(mapRef.current.rotateControl);
      } else {
        // Use default rotate control when no natural bearing is specified
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mapRef.current.rotateControl = (L.control as any).rotate(rotateControlOptions);
        mapRef.current.addControl(mapRef.current.rotateControl);
      }
    }

    mapRef.current.layerControl = L.control.layers(baseLayers, {});
    mapRef.current.baseLayers.push(streets);
    mapRef.current.baseLayers.push(satellite);

    if (!options.hideCurrentLocation) {
      CurrentLocation({ title: translate(LanguageKey.current_location_key) }).addTo(
        mapRef.current
      );
    }

    if (!options.hideOverview) {
      ControlOverview({ title: translate(LanguageKey.zoom_see_all_assets_key) }).addTo(
        mapRef.current
      );
    }

    if (options.showBoundFilter) {
      ControlBoundFilter({ title: translate(LanguageKey.map_bound_filter_key) }).addTo(
        mapRef.current
      );
    }

    mapRef.current.addGeozoneLayer();
    mapRef.current.addAssetLayer();
    mapRef.current.addInfraLayer();
    mapRef.current.addNetworkLayer();
    mapRef.current.addHeatMapLayer();
    // mapRef.current.addCustomLayers();
    if (options.searchBox) {
      initSearchBox();
    }
    if (!options.hideFullScreen) {
      initFullScreen();
    }

    L.control.scale({ imperial: false }).addTo(mapRef.current);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapRef.current.on("baselayerchange", function (e: any) {
      window.defaultMap = e.name;
    });

    mapRef.current.setMaxBounds([
      [-90, -180],
      [90, 180],
    ]);

    // Create a custom pane (under rotatePane when rotation is enabled so it rotates with the map)
    const customPaneName = "customPane";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rotateParent = (mapRef.current as any)._rotatePane || undefined;
    mapRef.current.createPane(customPaneName, rotateParent);
    mapRef.current.getPane(customPaneName).style.zIndex = "3";

    // Create a geozone pane (above sitemaps and overlayPane, below shadowPane; under rotatePane so geozones rotate with the map)
    const geozonePaneName = "geozonePane";
    mapRef.current.createPane(geozonePaneName, rotateParent);
    mapRef.current.getPane(geozonePaneName).style.zIndex = "5";

    // Add custom attribution manually
    L.control
      .attribution({
        position: "bottomright",
        prefix: false,
      })
      .addTo(mapRef.current);

    // Disable interactions if the prop is true
    if (disableInteractions) {
      mapRef.current.dragging.disable();
      mapRef.current.scrollWheelZoom.disable();
      mapRef.current.touchZoom.disable();
      mapRef.current.doubleClickZoom.disable();
      mapRef.current.boxZoom.disable();
    }

    // Watch container for size changes to fix tile rendering in responsive layouts
    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });
    if (domMap.current) {
      resizeObserver.observe(domMap.current);
    }

    // setMapLoaded(true);
    onMapLoaded?.();
    return () => {
      resizeObserver.disconnect();
      // Cleanup when component is unmounted
      if (mapRef.current) {
        try {
          // Destroy map instance - wrapped in try-catch because
          // leaflet-distortableimage may throw errors if it tries to
          // unbind listeners from an already-destroyed map
          mapRef.current.remove();
        } catch {
          // Ignore errors during map cleanup - this can happen when
          // distortable image layers try to call off() on null map
        }
        mapRef.current = null;
      }
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableInteractions]);

  // useEffect(() => {
  //   if (mapLoaded) {
  //     onMapLoaded && onMapLoaded();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [mapLoaded]);

  const initSearchBox = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let searchPosition: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const geocoder = new ((L.Control as any).geocoder)({
      position: "topleft",
      collapsed: false,
      defaultMarkGeocode: false,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      geocoder: new (LocationIQ as any)(key),
      placeholder: translate(LanguageKey.search_for_a_location_key),
    });
    
    geocoder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .on("markgeocode", function (e: any) {
        const bbox = e.geocode.bbox;

        let position;
        if (bbox.getSouthEast().distanceTo(bbox.getNorthWest()) === 0) {
          mapRef.current.setView(bbox.getSouthEast(), 21);
          position = bbox.getSouthEast();
        } else {
          const bounds = L.latLngBounds([
            bbox.getSouthEast(),
            bbox.getNorthWest(),
          ]);
          mapRef.current.fitBounds(bounds);
          position = mapRef.current.getCenter();
        }
        if (searchPosition) {
          searchPosition.remove();
        }

        searchPosition = L.marker(position, {
          title: translate(LanguageKey.search_location_key),
          icon: L.icon({
            iconUrl: CombinedShapeIcon,
            iconAnchor: [11, 10],
            iconSize: [22, 20],
          }),
        }).addTo(mapRef.current);
      });
      
    geocoder.addTo(mapRef.current);
  };

  const initFullScreen = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (L.control as any)
      .fullscreen({
        position: "bottomright", // change the position of the button can be topleft, topright, bottomright or bottomleft, defaut topleft
        title: translate(LanguageKey.show_me_the_fullscreen_key), // change the title of the button, default Full Screen
        titleCancel: translate(LanguageKey.exit_fullscreen_mode_key), // change the title of the button when fullscreen is on, default Exit Full Screen
        content: null, // change the content of the button, can be HTML, default null
        forceSeparateButton: true, // force seperate button to detach from zoom buttons, default false
        forcePseudoFullscreen: false, // force use of pseudo full screen even if full screen API is available, default false
        fullscreenElement: false, // Dom element to render in full screen, false by default, fallback to mapRef.current._container
      })
      .addTo(mapRef.current);
  };

  return (
    <div
      ref={domMap}
      className={`p-2 snt-map ${className}`}
      style={{ width: width, height: height }}
    >
      {React.Children.map(children, (child, i) => {
        if (mapRef.current && child && React.isValidElement(child)) {
          // Don't add map prop to React.Fragment as it only accepts key and children props
          if (child.type === React.Fragment) {
            return child;
          }
          return React.createElement(child.type, {
            ...(child.props || {}),
            map: mapRef.current,
            key: i,
          });
        }

        return child;
      })}
    </div>
  );
};
export default CommonMapReact;